// SPDX-FileCopyrightText: Copyright (c) 2025-2026 provide.io llc. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
/**
 * Bounded queue controls for telemetry signal paths.
 * Mirrors Python provide.telemetry.backpressure.
 */
import { _droppedField, _incrementHealth } from './health';
const DEFAULT_POLICY = { maxLogs: 0, maxTraces: 0, maxMetrics: 0 };
let _policy = { ...DEFAULT_POLICY };
let _tokenCounter = 1;
const _acquired = new Map([
    ['logs', new Set()],
    ['traces', new Set()],
    ['metrics', new Set()],
]);
export function setQueuePolicy(policy) {
    _policy = { ..._policy, ...policy };
}
export function getQueuePolicy() {
    return { ..._policy };
}
function _maxFor(signal) {
    if (signal === 'logs')
        return _policy.maxLogs;
    if (signal === 'traces')
        return _policy.maxTraces;
    return _policy.maxMetrics;
}
export function tryAcquire(signal) {
    const max = _maxFor(signal);
    if (max <= 0)
        return { signal, token: 0 };
    const set = _acquired.get(signal);
    // Stryker disable next-line ConditionalExpression: defensive guard — _acquired is initialized with all three signal keys, so get() never returns undefined
    /* v8 ignore next */
    if (!set)
        return null;
    if (set.size >= max) {
        _incrementHealth(_droppedField(signal));
        return null;
    }
    const token = _tokenCounter++;
    set.add(token);
    return { signal, token };
}
export function release(ticket) {
    // Stryker disable next-line ConditionalExpression: token=0 is the unlimited-queue sentinel; delete(0) from set is always a no-op
    if (ticket.token === 0)
        return;
    // Stryker disable next-line OptionalChaining: signal is always a key in _acquired (initialized in the Map constructor)
    _acquired.get(ticket.signal)?.delete(ticket.token);
}
export function _resetBackpressureForTests() {
    _policy = { ...DEFAULT_POLICY };
    _tokenCounter = 1;
    for (const set of _acquired.values())
        set.clear();
}
